import React, { useEffect, useState } from "react";
import NewSpendingDemandFormView from "./NewSpendingDemandView";
import { useSingleSpendingDemand, useSpendingDemand } from "../../hooks";
import { Project } from "../../../project/types/project.type";
import { SpendingDemand, SpendingDemandTypes } from "../../types/spendingDemand.types";
import SpendingDemandConfirmation from "./SpendingDemandConfirmation";

import AddProjectBtn from "../../../team/components/CreateTeam/CreateTeamForm/AddProjectBtn/AddProjectBtn";
import ProjectInfoCardView from "../../../project/components/ProjectInfoCardView/ProjectInfoCardView";
import { useSingleProject } from "../../../project/hooks";
import { extractAxoisErrorsMessage } from "../../../errorHandler/utils/errors.utils";
import { AxiosError } from "axios";
import { errorCodes } from "../../../errorHandler/types/errorCode";
import FileListManager, {
  FileUrl,
} from "../../../../components/molecules/FileListManager/FileListManager";
import { getFileUrlFromId } from "../../../../utils/fileReader";
import { useBoxAssociationFromBoxId, useSingleBoxAssociation } from "../../../boxAssociation/hooks/useBoxAssociation";
import useUserAuth from "../../../auth/hook/useUserAuth";
import { userRolesCode } from "../../../auth/types/user.types";
import DeleteFilePasswordModal from "../../../../components/molecules/DeleteFilePasswordModal/DeleteFilePasswordModal";

const ADMIN_ROLES_NEW_SPENDING_DEMAND: number[] = [
  userRolesCode.ADMIN,
  userRolesCode.SUPER_ADMIN,
  userRolesCode.ADMIN_PLUS,
];

export interface NewSpendingDemandData {
  description?: string;
  netAmount?: number;
  grossAmount?: number;
  project?: Project | undefined;
  category?: string;
  isAlreadyPaid?: boolean;
  paymentDate?: Date;
  spendingDemandType?: SpendingDemandTypes;
  boxId?: string;
}

export interface ErrorStatus {
  isDescriptionError: boolean;
  isNetAmountError: boolean;
  isGrossAmountError: boolean;
  paymentDateError: boolean;
  spendingDemandTypeError: boolean;
  projectError: boolean;
  boxIdError: boolean;
  allGood: boolean;
  message: string | null;
}

const INITIAL_ERROR_STATUS: ErrorStatus = {
  isDescriptionError: false,
  isNetAmountError: false,
  isGrossAmountError: false,
  paymentDateError: false,
  spendingDemandTypeError: false,
  projectError: false,
  boxIdError: false,
  allGood: true,
  message: null,
};
const NEW_SPENDING_DEMANDE_DATA_DEFAULT: NewSpendingDemandData = {
  description: "",
  netAmount: 0,
  grossAmount: 0,
  project: undefined,
  category: "",
  isAlreadyPaid: false,
  paymentDate: new Date(),
  spendingDemandType: undefined,
  boxId: "",
};

export const checkValidityOfNewSpendingDemandData = (
  data: SpendingDemand
): ErrorStatus => {
  let errorStatus = { ...INITIAL_ERROR_STATUS };
  let message = [];

  if (!data.spending_demand_desc || data.spending_demand_desc.length < 5) {
    errorStatus.isDescriptionError = true;
    errorStatus.allGood = false;
    message.push("La description doit faire au moins 5 caractères");
  }

  if (!data.spending_demand_amount || data.spending_demand_amount <= 0) {
    errorStatus.isNetAmountError = true;
    errorStatus.allGood = false;
    message.push("Le montant net doit être supérieur à 0");
  }

  if (!data.spending_demand_amountTTC || data.spending_demand_amountTTC <= 0) {
    errorStatus.isGrossAmountError = true;
    errorStatus.allGood = false;
    message.push("Le montant TTC doit être supérieur à 0");
  }

  if (!data.spending_demand_date) {
    errorStatus.paymentDateError = true;
    errorStatus.allGood = false;
    message.push("La date de paiement est invalide");
  }

  if (!data.spending_demand_type) {
    errorStatus.spendingDemandTypeError = true;
    errorStatus.allGood = false;
    message.push("Le type de dépense est obligatoire");
  }

  // Validation conditionnelle selon le type
  if (data.spending_demand_type === "PROJECT" && !data.spending_demand_project) {
    errorStatus.projectError = true;
    errorStatus.allGood = false;
    message.push("Un projet est obligatoire pour les dépenses de type Projet");
  }

  if (data.spending_demand_type === "BOX" && !data.spending_demand_box_association_id) {
    errorStatus.boxIdError = true;
    errorStatus.allGood = false;
    message.push("Un Box ID est obligatoire pour les dépenses de type Box");
  }

  if (message.length > 0) {
    errorStatus.message = message.join(", ");
  } else {
    errorStatus.message = null;
  }

  return errorStatus;
};

const toPaymentDateISO = (date: Date | string | undefined): string => {
  if (!date) return "";
  if (date instanceof Date) return date.toISOString();
  return new Date(date as string).toISOString();
};

export const convertFormDtaToNewSpendingDemand = (
  data: NewSpendingDemandData,
  boxAssociationId?: string
): Partial<SpendingDemand> => {
  const paymentDateISO = toPaymentDateISO(data.paymentDate);
  return {
    spending_demand_amount: parseFloat(data.grossAmount?.toString() || "0"),
    spending_demand_amountTTC: parseFloat(data.grossAmount?.toString() || "0"),
    spending_demand_desc: data.description,
    spending_demand_date: paymentDateISO || undefined,
    spending_demand_project: data.project?.project_id
      ? data.project.project_id
      : undefined,
    spending_demand_type: data.spendingDemandType,
    spending_demand_box_association_id: boxAssociationId || undefined,
  };
};

export default function NewSpendingDemandForm({
  isUpdate,
}: {
  isUpdate?: boolean;
}) {
  const { createNewSpendingDemand } = useSpendingDemand({ disableQuery: true });
  const {
    modifySpendingDemand,
    singleSpendingDemandData,
    isLoading: isLoadingSingleSpendingDemand,
    getListFilesOfSpendingDemand,
    deleteFileOfSpendingDemand
  } = useSingleSpendingDemand(undefined, { disableQuery: !isUpdate});
  const [listFiles, setFileList] = useState<FileUrl[]>([]);
  const { userInfo } = useUserAuth();
  const isAdmin =
    userInfo?.user_role !== undefined && ADMIN_ROLES_NEW_SPENDING_DEMAND.includes(userInfo.user_role);
  const [fileIdPendingDelete, setFileIdPendingDelete] = useState<string | null>(null);
  const [deleteFileError, setDeleteFileError] = useState<string | null>(null);

  const [NewSpendingDemandData, setNewSpendingDemandData] =
    useState<NewSpendingDemandData>(NEW_SPENDING_DEMANDE_DATA_DEFAULT);
  
  // État pour stocker le boxAssociationId récupéré
  const [boxAssociationId, setBoxAssociationId] = useState<string | undefined>(undefined);
  
  // État pour le debounce du boxId
  const [debouncedBoxId, setDebouncedBoxId] = useState<string | undefined>(undefined);
  
  // Récupérer le boxId à partir du boxAssociationId lors du chargement d'une demande existante
  const existingBoxAssociationId = isUpdate && singleSpendingDemandData?.spending_demand_box_association_id
    ? singleSpendingDemandData.spending_demand_box_association_id
    : undefined;
  
  const {
    boxAssociation: existingBoxAssociation,
  } = useSingleBoxAssociation(existingBoxAssociationId, { disableQuery: !existingBoxAssociationId });
  
  // Debounce du boxId pour éviter trop de requêtes pendant la saisie
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBoxId(
        NewSpendingDemandData.spendingDemandType === "BOX" && NewSpendingDemandData.boxId
          ? NewSpendingDemandData.boxId
          : undefined
      );
    }, 500); // Attendre 500ms après la dernière frappe

    return () => clearTimeout(timer);
  }, [NewSpendingDemandData.boxId, NewSpendingDemandData.spendingDemandType]);
  
  // Validation de la BoxId en temps réel (avec debounce)
  const {
    boxAssociation: validatedBoxAssociation,
    isLoading: isValidatingBox,
    isError: isBoxError,
  } = useBoxAssociationFromBoxId(
    debouncedBoxId,
    { disableQuery: !debouncedBoxId || NewSpendingDemandData.spendingDemandType !== "BOX" }
  );

  // Mettre à jour boxAssociationId quand la validation réussit
  useEffect(() => {
    if (validatedBoxAssociation?.id) {
      setBoxAssociationId(validatedBoxAssociation.id);
    } else {
      setBoxAssociationId(undefined);
    }
  }, [validatedBoxAssociation]);
  
  // Initialiser boxAssociationId et boxId lors du chargement d'une demande existante
  useEffect(() => {
    if (isUpdate && existingBoxAssociation?.id) {
      setBoxAssociationId(existingBoxAssociation.id);
      // Mettre à jour le boxId dans le formulaire
      if (existingBoxAssociation.boxId) {
        setNewSpendingDemandData((prev) => ({
          ...prev,
          boxId: existingBoxAssociation.boxId,
        }));
      }
    }
  }, [isUpdate, existingBoxAssociation]);

  useEffect(() => {
    if (isUpdate && singleSpendingDemandData) {
      setNewSpendingDemandData((prev) => ({
        description: singleSpendingDemandData.spending_demand_desc || "",
        netAmount: singleSpendingDemandData.spending_demand_amount || 0,
        grossAmount: singleSpendingDemandData.spending_demand_amountTTC || 0,
        paymentDate: new Date(
          singleSpendingDemandData.spending_demand_date || ""
        ),
        category: singleSpendingDemandData.spending_demand_category || "",
        project: singleSpendingDemandData.spending_demand_project as Project,
        spendingDemandType: singleSpendingDemandData.spending_demand_type,
        boxId: prev.boxId || "", // Garder le boxId existant ou vide
      }));
      if(getListFilesOfSpendingDemand.data){
       const  listFileUrlDist : FileUrl[] | undefined = getListFilesOfSpendingDemand.data?.data?.map((file)=>({
        ...(new File([], file.spdf_file_name || "", { type: file.spdf_file_type || "" })),
        url: getFileUrlFromId(file.spdf_file_id || ""),
        name: file.spdf_file_name || "",
        type: file.spdf_file_type || "",
        size: 0,
        toNotUpload: true,
        // Persisted file: deletion is admin-only and password-gated (mirrors FilesList.tsx).
        // Non-admins get no onDelete here, so FileListManager renders no delete button for it.
        onDelete: isAdmin
          ? () => {
              setDeleteFileError(null);
              setFileIdPendingDelete(file.spdf_id);
            }
          : undefined,
       }))

       const localListFileUrl = listFiles ?  listFiles.filter((f) => f.toNotUpload !== true) : []

       setFileList([ ...(listFileUrlDist || []),...localListFileUrl])
      }
    }
  }, [singleSpendingDemandData, getListFilesOfSpendingDemand.data, deleteFileOfSpendingDemand.isSucess, deleteFileOfSpendingDemand.isError]); //eslint-disable-line

  const handleCloseDeleteFileModal = () => {
    setFileIdPendingDelete(null);
    setDeleteFileError(null);
  };

  const handleConfirmDeletePersistedFile = (password: string) => {
    if (!fileIdPendingDelete) return;
    setDeleteFileError(null);
    deleteFileOfSpendingDemand.run(
      { spendingDemandFileId: fileIdPendingDelete, password },
      {
        onSuccess: () => {
          setFileIdPendingDelete(null);
        },
        onError: (error: any) => {
          const status = error?.response?.status;
          setDeleteFileError(
            status === 401 || status === 403
              ? "Mot de passe incorrect ou accès refusé."
              : "Une erreur est survenue, veuillez réessayer."
          );
        },
      }
    );
  };

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Placeholder for project options (should b) fetched from API or props)
  const projectOptions: { id: string | number; name: string }[] = [];

  // Map Project to ProjectOption for the view
  const selectedProjectOption = NewSpendingDemandData.project
    ? {
        id: (NewSpendingDemandData.project as any).id,
        name: (NewSpendingDemandData.project as any).name,
      }
    : undefined;

  const handleChange = (key: keyof NewSpendingDemandData, value: any) => {
    setNewSpendingDemandData((prevData) => {
      const newData = {
        ...prevData,
        [key]: value,
      };

      // Logique de réinitialisation selon le type
      if (key === "spendingDemandType") {
        // Réinitialiser les champs spécifiques selon le nouveau type
        if (value === "PROJECT") {
          // Pour PROJECT, réinitialiser boxId
          newData.boxId = "";
          setBoxAssociationId(undefined);
        } else if (value === "BOX") {
          // Pour BOX, réinitialiser project
          newData.project = undefined;
        } else if (value === "CURRENT") {
          // Pour CURRENT, réinitialiser project et boxId
          newData.project = undefined;
          newData.boxId = "";
          setBoxAssociationId(undefined);
        }
      }
      
      // Réinitialiser boxAssociationId si boxId change
      if (key === "boxId") {
        setBoxAssociationId(undefined);
      }

      return newData;
    });
    setErrorMessage(null); // Clear error on change
  };

  // Adapter for the view's onChange prop (accepts string key)
  const handleViewChange = (key: string, value: any) => {
    handleChange(key as keyof NewSpendingDemandData, value);
  };

  const handleSubmit = (e?: React.FormEvent) => {
    console.log("Submit new spending demand");
    e?.preventDefault();

    // Vérifier que boxAssociationId est disponible si type BOX
    if (NewSpendingDemandData.spendingDemandType === "BOX" && !boxAssociationId) {
      setErrorMessage("Veuillez entrer un Box ID valide");
      return;
    }

    const spendingDemandData = convertFormDtaToNewSpendingDemand(
      NewSpendingDemandData,
      boxAssociationId
    );
    const errorStatus = checkValidityOfNewSpendingDemandData(
      spendingDemandData as SpendingDemand
    );

    if (!errorStatus.allGood) {
      setErrorMessage(errorStatus.message);
      return;
    }

    createNewSpendingDemand.mutate(
      { spendingDemand: spendingDemandData, files: listFiles },
      {
        onSuccess: (data) => {
          console.log("data spending demand ", data);
          setIsSuccess(true);
        },
        onError: (error) => {
          setIsSuccess(false);
          const msgErr = extractAxoisErrorsMessage(error as AxiosError);
          if (
            msgErr.includes(errorCodes.SPENDING_DEMAND_NO_TEAM_COMPTA_FOUND)
          ) {
            setErrorMessage(
              "Désolé aucun équipe comptabilité trouvée pour traiter cette demande, veuillez contacter l'administrateur"
            );
          } else {
            setErrorMessage(
              "Erreur lors de la création de la demande : " + msgErr
            );
          }
        },
      }
    );
  };


  const handleModify = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    // Vérifier que boxAssociationId est disponible si type BOX
    if (NewSpendingDemandData.spendingDemandType === "BOX" && !boxAssociationId) {
      setErrorMessage("Veuillez entrer un Box ID valide");
      return;
    }
    
    const spendingDemandData = convertFormDtaToNewSpendingDemand(
      NewSpendingDemandData,
      boxAssociationId
    );
    const errorStatus = checkValidityOfNewSpendingDemandData(
      spendingDemandData as SpendingDemand
    );

    if (!errorStatus.allGood) {
      setErrorMessage(errorStatus.message);
      return;
    }

    if (isUpdate) {
      modifySpendingDemand.run(
        { spendingDemandDto: spendingDemandData, files: listFiles },
        {
          onSuccess: (data) => {
            setIsSuccess(true);
          },
          onError: (error) => {
            setIsSuccess(false);
            setErrorMessage("Erreur lors de la modification de la demande.");
          },
        }
      );
    }
  };

  if (isUpdate && isLoadingSingleSpendingDemand) {
    return <div>Chargement de la demande de dépense...</div>;
  }

  if (isUpdate && !singleSpendingDemandData) {
    return <div>Demande de dépense non trouvée</div>;
  }

  return (
    <>
      {isSuccess ? (
        <SpendingDemandConfirmation isUpdate={isUpdate} />
      ) : (
        <NewSpendingDemandFormView
          description={NewSpendingDemandData.description || ""}
          grossAmount={NewSpendingDemandData.grossAmount || 0}
          paymentDate={toPaymentDateISO(NewSpendingDemandData.paymentDate)}
          category={NewSpendingDemandData.category || ""}
          isAlreadyPaid={NewSpendingDemandData.isAlreadyPaid || false}
          project={selectedProjectOption}
          projectOptions={projectOptions}
          spendingDemandType={NewSpendingDemandData.spendingDemandType}
          boxId={NewSpendingDemandData.boxId || ""}
          boxIdValidationStatus={{
            isValid: !!boxAssociationId,
            isLoading: isValidatingBox,
            isError: isBoxError,
          }}
          onChange={handleViewChange}
          onSubmit={isUpdate ? handleModify : handleSubmit}
          isUpdate={isUpdate}
          errorMessage={errorMessage}
          projectPickerRender={() => (
            <>
              {NewSpendingDemandData.spendingDemandType === "PROJECT" && (
                <>
                  {NewSpendingDemandData.project?.project_id ? (
                    <ProjectInformation
                      projectId={NewSpendingDemandData.project?.project_id}
                      onDelete={() => handleChange("project", null)}
                    />
                  ) : (
                    <AddProjectBtn
                      setProject={(project) => handleChange("project", project)}
                      label="Ajouter un projet"
                      isUserMember={true}
                    />
                  )}
                </>
              )}
            </>
          )}
          FilePickerRender={() => (
            <FileListManager
              label="Justificatifs"
              messageEmpty="Aucun justificatif"
              limitFiles={10}
              addLabel="Ajouter un justificatif"
              accept="image/*,.pdf,.xls,.xlsx,.xlsm,.xlsb,.csv"
              listFiles={listFiles}
              setFileList={setFileList}
              isLoading={isUpdate && (getListFilesOfSpendingDemand.isPending  || deleteFileOfSpendingDemand.isPending)}

            />
          )}
        />
      )}
      {fileIdPendingDelete && (
        <DeleteFilePasswordModal
          onClose={handleCloseDeleteFileModal}
          onConfirm={handleConfirmDeletePersistedFile}
          isLoading={deleteFileOfSpendingDemand.isPending}
          error={deleteFileError}
        />
      )}
    </>
  );
}

export const ProjectInformation = ({
  projectId,
  onDelete,
}: {
  projectId?: string;
  onDelete?: () => void;
}) => {
  const { querySingleProject } = useSingleProject(projectId);
  if (querySingleProject.isLoading) {
    return <div>Chargement du projet...</div>;
  }

  if (querySingleProject.isError) {
    return <div>Erreur lors du chargement du projet</div>;
  }

  if (!querySingleProject.data) {
    return <div>Projet non trouvé</div>;
  }

  return (
    <ProjectInfoCardView
      title={querySingleProject.data?.project_name || ""}
      description={querySingleProject.data?.project_desc || ""}
      onDelete={onDelete}
      project_status={querySingleProject.data?.project_status}
    />
  );
};
